import { useEffect, useState } from 'react';
import { getOpenApiSpecUrl, parseSwagger2Document } from './parseOpenApi';
import type { ParsedOpenApi, Swagger2Document } from './types';

type OpenApiSpecState = {
  data: ParsedOpenApi | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

export function useOpenApiSpec(): OpenApiSpecState {
  const [data, setData] = useState<ParsedOpenApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const url = getOpenApiSpecUrl();

    fetch(url, { signal: controller.signal, headers: { accept: 'application/json' } })
      .then(async res => {
        if (!res.ok) {
          throw new Error(`Failed to load OpenAPI spec (${res.status})`);
        }
        return res.json() as Promise<Swagger2Document>;
      })
      .then(doc => {
        if (!doc?.swagger && !(doc as { openapi?: string }).openapi) {
          throw new Error('Invalid OpenAPI document');
        }
        setData(parseSwagger2Document(doc));
      })
      .catch(err => {
        if (err?.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Unable to load API documentation');
        setData(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [reloadToken]);

  return {
    data,
    loading,
    error,
    reload: () => setReloadToken(t => t + 1),
  };
}
